import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Store, Building2, MapPin, Phone, Mail, Lock, User, FileText, ArrowRight, Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUSINESS_TYPES = [
  { value: 'restaurant', label: 'Restaurant', icon: '🍽️' },
  { value: 'epicerie', label: 'Épicerie', icon: '🛒' },
  { value: 'supermarche', label: 'Supermarché', icon: '🏪' },
  { value: 'producteur', label: 'Producteur local', icon: '🌾' },
  { value: 'marche', label: 'Marché', icon: '🥬' },
  { value: 'boucherie', label: 'Boucherie', icon: '🥩' },
  { value: 'poissonnerie', label: 'Poissonnerie', icon: '🐟' },
  { value: 'boulangerie', label: 'Boulangerie', icon: '🥖' },
  { value: 'autre', label: 'Autre', icon: '🏬' }
];

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    features: [
      'Profil boutique complet',
      'Catalogue jusqu\'à 50 produits',
      'Page publique',
      'Support par email'
    ],
    notIncluded: ['Campagnes publicitaires', 'Statistiques', 'Accès base de prix']
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    popular: true,
    features: [
      'Tout Starter +',
      'Catalogue jusqu\'à 500 produits',
      '5 campagnes/mois',
      'Statistiques de base',
      'Support prioritaire'
    ],
    notIncluded: ['Accès base de prix']
  },
  {
    id: 'premium',
    name: 'Premium',
    price: 149,
    features: [
      'Tout Pro +',
      'Catalogue illimité',
      'Campagnes illimitées',
      'Statistiques avancées',
      'Accès base de prix référence',
      'Account manager dédié'
    ],
    notIncluded: []
  }
];

export default function BusinessRegisterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    contact_name: '',
    contact_phone: '',
    business_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    business_type: '',
    description: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    if (formData.siret.length !== 14) {
      toast.error('Le SIRET doit contenir 14 chiffres');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/business/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          contact_name: formData.contact_name,
          contact_phone: formData.contact_phone,
          business_name: formData.business_name,
          siret: formData.siret,
          address: formData.address,
          city: formData.city,
          postal_code: formData.postal_code,
          business_type: formData.business_type,
          description: formData.description
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de l\'inscription');
      }

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      toast.success('Bienvenue ! Votre compte pro a été créé avec 1 mois d\'essai gratuit 🎉');
      navigate('/business/dashboard');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl">👨‍🍳</span>
            <span className="font-bold text-charcoal">Gusto</span>
          </Link>
          <Link to="/login" className="text-sm text-muted-foreground hover:text-primary">
            Déjà inscrit ? Se connecter
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            1 mois d'essai gratuit
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-charcoal mb-4">
            Développez votre commerce avec Gusto
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Créez votre vitrine en ligne, publiez votre catalogue, lancez des campagnes ciblées 
            et accédez à des statistiques de marché exclusives.
          </p>
        </div>

        {/* Plans Overview */}
        {step === 1 && (
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-center mb-6">Choisissez votre forfait</h2>
            <p className="text-center text-muted-foreground mb-8">Engagement 12 mois • 1er mois offert</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {PLANS.map((plan) => (
                <div 
                  key={plan.id}
                  className={`bg-white rounded-2xl p-6 shadow-lg border-2 transition-all ${
                    plan.popular ? 'border-primary ring-2 ring-primary/20 scale-105' : 'border-transparent hover:border-primary/30'
                  }`}
                >
                  {plan.popular && (
                    <div className="bg-primary text-white text-xs font-medium px-3 py-1 rounded-full inline-block mb-4">
                      Le plus populaire
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-charcoal">{plan.name}</h3>
                  <div className="mt-2 mb-4">
                    <span className="text-3xl font-bold text-charcoal">{plan.price}€</span>
                    <span className="text-muted-foreground">/mois</span>
                  </div>
                  
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                    {plan.notIncluded.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="w-4 h-4 mt-0.5 flex-shrink-0 text-center">-</span>
                        <span className="line-through">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Button 
                size="lg" 
                onClick={() => setStep(2)}
                className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
              >
                Commencer mon essai gratuit
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-xs text-muted-foreground mt-2">Aucune carte bancaire requise pour l'essai</p>
            </div>
          </div>
        )}

        {/* Registration Form */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full flex items-center justify-center">
                  <Store className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-charcoal">Créer votre compte Pro</h2>
                  <p className="text-sm text-muted-foreground">1 mois d'essai gratuit, sans engagement</p>
                </div>
              </div>

              {/* Contact Info */}
              <div className="mb-8">
                <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <User className="w-4 h-4" /> Informations de contact
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Nom complet *</label>
                    <Input
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Téléphone</label>
                    <Input
                      name="contact_phone"
                      value={formData.contact_phone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Email *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="contact@maboutique.fr"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Mot de passe *</label>
                    <Input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-1 block">Confirmer le mot de passe *</label>
                    <Input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Business Info */}
              <div className="mb-8">
                <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" /> Informations de l'entreprise
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">Nom de la boutique *</label>
                    <Input
                      name="business_name"
                      value={formData.business_name}
                      onChange={handleChange}
                      placeholder="Ma Super Épicerie"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">SIRET *</label>
                    <Input
                      name="siret"
                      value={formData.siret}
                      onChange={handleChange}
                      placeholder="12345678901234"
                      maxLength={14}
                      required
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">Type d'activité *</label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {BUSINESS_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, business_type: type.value })}
                          className={`p-3 rounded-lg border-2 text-center transition-all ${
                            formData.business_type === type.value
                              ? 'border-primary bg-primary/5'
                              : 'border-gray-200 hover:border-primary/50'
                          }`}
                        >
                          <span className="text-2xl block mb-1">{type.icon}</span>
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="mb-8">
                <h3 className="font-semibold text-charcoal mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" /> Adresse
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-1 block">Adresse *</label>
                    <Input
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="123 Rue de la Paix"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Code postal *</label>
                    <Input
                      name="postal_code"
                      value={formData.postal_code}
                      onChange={handleChange}
                      placeholder="75001"
                      maxLength={5}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Ville *</label>
                    <Input
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="Paris"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="mb-8">
                <label className="text-sm font-medium mb-1 block">Description (optionnel)</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Décrivez votre activité en quelques mots..."
                  className="w-full p-3 border rounded-lg resize-none h-24"
                />
              </div>

              <div className="flex gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !formData.business_type}
                  className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
                >
                  {loading ? 'Création en cours...' : 'Créer mon compte Pro'}
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                En créant un compte, vous acceptez nos conditions d'utilisation et notre politique de confidentialité.
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}